import { OnConnect, OnConnectEnd, OnConnectStart, OnEdgesChange, OnNodesChange, addEdge, applyEdgeChanges, applyNodeChanges, useReactFlow } from "reactflow";
import NodeStore, { FunctionListItem } from "../stores/NodeStore";
import { MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import { client } from "../../../utils/axios-client";

const useDesigner = () => {
    const chat = NodeStore.useState(s => s.chat);
    const isLoading = NodeStore.useState(s => s.isLoading);
    const selectedHandleId = useRef<string | null>(null);
    const selectedHandleType = useRef<string | null>(null);
    const connectingNodeId = useRef<string | null>(null);
    const functions = NodeStore.useState(s => s.functions);

    const { screenToFlowPosition } = useReactFlow();
    const [newNodeRequest, setNewNodeRequest] = useState({
        clientX: 0,
        clientY: 0,
        nodeType: '',
        isOpen: false
    })

    useEffect(() => {
        loadFunctions();
    }, [])

    const loadFunctions = async () => {
        const response = await client.get<FunctionListItem[]>('/api/chat/functions');

        NodeStore.update(s => {            
            s.functions = response.data;
        })
    }

    const onNodesChange: OnNodesChange = useCallback(
        (changes) => {
            NodeStore.update((s, o) => {
                const temp = structuredClone(o.chat);
                temp.nodes = applyNodeChanges(changes, o.chat.nodes);

                s.isDirty = o.isLoading ? false : true;
                s.chat = temp;
                s.isLoading = false;
            });
        },
        [chat.nodes],
    );

    const onEdgesChange: OnEdgesChange = useCallback(
        (changes) => {
            NodeStore.update((s, o) => {
                const temp = structuredClone(o.chat);
                temp.edges = applyEdgeChanges(changes, o.chat.edges);

                s.isDirty = true;
                s.chat = temp;
            });
        },
        [chat.nodes],
    );

    const onConnect: OnConnect = useCallback(
        (params) => {
            // reset the start node on connections
            connectingNodeId.current = null;

            NodeStore.update((s, o) => {
                const temp = structuredClone(o.chat);

                temp.edges = addEdge({
                    ...params,
                    type: 'smoothstep',
                }, o.chat.edges);

                s.isDirty = true;
                s.chat = temp;
            });
        },
        [chat.nodes],
    );

    const onConnectStart: OnConnectStart = useCallback((_, { nodeId, handleId, handleType }) => {
        connectingNodeId.current = nodeId;
        selectedHandleId.current = handleId;
        selectedHandleType.current = handleType;
    }, [chat.nodes]);

    const onConnectEnd: OnConnectEnd = useCallback(
        (event) => {            
            if (!connectingNodeId.current || !event.target) return;

            if (event.target instanceof Element) {

                const targetIsPane = event.target.classList.contains('react-flow__pane');                

                if (targetIsPane) {
                    const sourceNode = chat.nodes.find(s => s.id === connectingNodeId.current);

                    // we need to remove the wrapper bounds, in order to get the correct position

                    let nodeType = 'searchNodesNode'
                    
                    if (event instanceof MouseEvent) {
                        switch (selectedHandleId.current) {
                            case "inp_imageNode":
                            case "src_imageNode":
                                nodeType = 'imageNode'
                                break;
                            case "inp_optionNode":
                            case "src_optionNode":
                                nodeType = 'optionNode';
                                break;
                            case "inp_captureResponseNode":
                            case "src_captureResponseNode":
                                nodeType = 'captureResponseNode';
                                break;
                            case "inp_yesNoNode":
                            case "src_yesNoNode":
                                nodeType = 'yesNoNode';
                                break;
                            case "inp_emailNode":
                            case "src_emailNode":
                                nodeType = 'emailNode';
                                break;
                            case "inp_promptNode":
                            case "src_promptNode":
                                nodeType = 'promptNode';
                                break;
                            case "inp_ticketNode":
                            case "src_ticketNode":
                                nodeType = 'ticketNode';
                                break;

                            case "inp_yesNode":
                            case "src_noNode":
                            case "inp_noNode":
                            case "src_yesNode":
                                nodeType = 'branchNode';
                                break;
                            default:
                                
                                break;
                        }

                        //if (nodeType = 'searchNodesNode') {
                        //    setNewNodeRequest({
                        //        nodeType,
                        //        clientX: event.clientX,
                        //        clientY: event.clientY,
                        //        isOpen: true
                        //    })
                        //}

                        //else {
                            createNode(nodeType, event.clientX, event.clientY);
                        //}                        
                    }
                }
            }
        },
        [screenToFlowPosition, chat.nodes],
    );

    const createNode = (type: string, clientX: number, clientY: number) => {
        const id = crypto.randomUUID().toString();

        const newNode = {
            id,
            position: screenToFlowPosition({
                x: clientX,
                y: clientY,
            }),
            data: { label: `Node ${id}`, isAction: type === 'emailNode' },
            origin: [0.5, 0.0],
            type: type
        };

        NodeStore.update((s, o) => {
            const temp = structuredClone(o.chat);
            temp.nodes = temp.nodes.concat(newNode);

            s.isDirty = true;
            s.chat = temp;
        });
        
        const node = chat.nodes.find(s => s.id === connectingNodeId.current!)

        let sourceHandle = selectedHandleId.current!
//        let targetHandle = "inp_" + node?.type;
        let targetHandle = "inp_" + type

        let source = connectingNodeId.current!;
        let target = id;
        
        if (selectedHandleId.current?.includes("inp_")) {
            sourceHandle = 'src_' + node?.type;
            targetHandle = selectedHandleId.current!;
            source = id;
            target = connectingNodeId.current!;
        }
        //} else if (type === 'promptNode') {
        //    targetHandle = 'promptNode';
        //}

        const singletonNodeTypes = [
            'questionNode',
            /*'answerNode',*/
            'captureResponseNode',
            'imageNode',
            'promptNode',
            'emailNode',
            'searchNodesNode',
            'ticketNode'
        ]

        NodeStore.update((s, o) => {
            const temp = structuredClone(o.chat);

            if (singletonNodeTypes.some(s => s === type)) {
                const existingEdges = temp.edges.filter(s => s.source == source)
                    .flatMap(s => s.id);


                temp.edges = temp.edges.filter(s => !existingEdges.includes(s.id));
            }
            
            //const tHandle = sourceHandle === 'src_playNode' ? 'inp_playNode' : targetHandle
            
            temp.edges = temp.edges.concat({ id, type: 'smoothstep', source: source, target: target, sourceHandle: sourceHandle, targetHandle: targetHandle });
            s.isDirty = true;
            s.chat = temp;
        });
    }

    const removeNode = (id: string) => {
        NodeStore.update((s, o) => {
            const temp = structuredClone(o.chat);

            temp.nodes = temp.nodes.filter(s => s.id !== id);

            s.isDirty = true;
            s.chat = temp;
        });
    }

    const replaceNodeWithType = (id: string, nodeType: string, functionId: string, isFunction: boolean, functionName: string) => {
        NodeStore.update((s, o) => {
            const temp = structuredClone(o.chat);

            const node = temp.nodes.find(s => s.id === id);

            if (node) {
                node.type = nodeType;               
                node.data.functionId = functionId;
                node.data.isFunction = isFunction;
                node.data.functionName = functionName;
                s.isDirty = true;
            }            
            
            s.chat = temp;
        });
    }

    return {
        onConnect,
        onConnectEnd,
        onConnectStart,
        onEdgesChange,
        onNodesChange,
        createNode,
        nodes: chat.nodes,
        newNodeRequest,
        removeNode,
        replaceNodeWithType,
        functions
    }
}

export default useDesigner;